import type { ContentBlock, HaltOption, OrderInsightPayload } from '../interfaces/chat';

/** Removes <thinking>…</thinking> reasoning blocks before display. */
export function stripThinking(text: string): string {
  return text.replace(/<thinking>[\s\S]*?<\/thinking>\s*/g, '').trim();
}

// Cap on how many levels of accidental re-stringification (a backend bug
// occasionally double- or triple-wraps a content-block array as a JSON
// string inside another block's `content` field) we'll unwrap before giving
// up and treating a value as literal text — guards against a pathological
// or hostile payload nesting arbitrarily deep.
const MAX_BLOCK_UNWRAP_DEPTH = 5;

/** A single block's text, unwrapping nested JSON-stringified block arrays. */
function extractBlockText(block: Record<string, unknown>, depth: number): string {
  const raw = typeof block.content === 'string' ? block.content : typeof block.text === 'string' ? block.text : '';
  if (!raw || depth >= MAX_BLOCK_UNWRAP_DEPTH) return raw;

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return extractBlocksText(parsed, depth + 1);
  } catch {
    // not JSON — this block's content/text is literal.
  }
  return raw;
}

function extractBlocksText(blocks: unknown[], depth: number): string {
  return blocks
    .map((b) => (b && typeof b === 'object' ? extractBlockText(b as Record<string, unknown>, depth) : ''))
    .join('');
}

/**
 * Extracts the displayable text from a chat.block / chat.halted payload. The
 * engine may send a plain string, a JSON array of content blocks, or a
 * `message` field (halts). Mirrors the archer-ai example client.
 *
 * A block's own `content`/`text` is occasionally itself a JSON-stringified
 * array of further blocks (a known backend double-wrapping bug) rather than
 * literal text — {@link extractBlockText} recurses into that case instead of
 * rendering the raw JSON string.
 */
export function extractContent(data: Record<string, unknown>): string {
  if (typeof data.message === 'string') return stripThinking(data.message);

  if (typeof data.content === 'string') {
    try {
      const parsed = JSON.parse(data.content);
      if (Array.isArray(parsed)) {
        return stripThinking(extractBlocksText(parsed, 0));
      }
    } catch {
      // not JSON — use as-is
    }
    return stripThinking(data.content);
  }

  if (typeof data.text === 'string') return stripThinking(data.text);

  const blocks = data.contentBlocks || data.blocks;
  if (Array.isArray(blocks)) {
    return stripThinking(
      blocks.map((b: Record<string, unknown>) => (typeof b.text === 'string' ? b.text : '')).join(''),
    );
  }

  return '';
}

/**
 * Normalizes one raw block object into a {@link ContentBlock}, unwrapping the
 * same accidental double-JSON-stringification handled in {@link extractContent}
 * — a `markdown` block whose `content` is itself a JSON-stringified block
 * array is spliced in place rather than rendered as literal text, and a
 * data block that got a JSON-stringified string in `content` instead of a
 * real `data` object (the "wrong key" failure mode called out in
 * wismoagent.md) is recovered by parsing it.
 */
function normalizeBlock(raw: Record<string, unknown>, depth: number): ContentBlock[] {
  const type = typeof raw.type === 'string' ? raw.type : undefined;
  if (!type) return [];

  if (type === 'markdown') {
    const content = typeof raw.content === 'string' ? raw.content : '';
    if (content && depth < MAX_BLOCK_UNWRAP_DEPTH) {
      try {
        const inner = JSON.parse(content);
        if (Array.isArray(inner)) return normalizeBlocks(inner, depth + 1);
      } catch {
        // literal markdown text — fall through.
      }
    }
    return [{ type: 'markdown', content }];
  }

  if ('data' in raw) return [{ type, data: raw.data }];

  if (typeof raw.content === 'string') {
    try {
      return [{ type, data: JSON.parse(raw.content) }];
    } catch {
      return [{ type, data: raw.content }];
    }
  }

  return [{ type, data: null }];
}

function normalizeBlocks(raw: unknown[], depth: number): ContentBlock[] {
  const out: ContentBlock[] = [];
  for (const item of raw) {
    if (item && typeof item === 'object') out.push(...normalizeBlock(item as Record<string, unknown>, depth));
  }
  return out;
}

/**
 * Extracts the typed content-block array from a chat.block payload, per the
 * WISMO order-details agent's contract (wismoagent.md). Returns `undefined`
 * when `data.content` isn't a JSON array of blocks at all (e.g. plain text
 * or a `message`-based halt payload) — callers should keep using
 * {@link extractContent} for the display text in that case.
 */
export function extractContentBlocks(data: Record<string, unknown>): ContentBlock[] | undefined {
  if (typeof data.content !== 'string') return undefined;
  try {
    const parsed = JSON.parse(data.content);
    if (!Array.isArray(parsed)) return undefined;
    const blocks = normalizeBlocks(parsed, 0);
    return blocks.length > 0 ? blocks : undefined;
  } catch {
    return undefined;
  }
}

/** Normalises the raw halt `options` array into typed {@link HaltOption}s. */
export function extractHaltOptions(raw: unknown): HaltOption[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const opts: HaltOption[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === 'string' ? e.id : undefined;
    const label = typeof e.label === 'string' ? e.label : undefined;
    if (!id || !label) continue;
    const description = typeof e.description === 'string' ? e.description : undefined;
    opts.push({ id, label, description });
  }
  return opts.length > 0 ? opts : undefined;
}

/**
 * Validates and normalises a `chat.order_insight` payload. Only structural
 * shape is checked here — health/confidence/root-cause *content* is trusted
 * as-is from the backend (the UI never re-derives it). Arrays default to `[]`
 * rather than being dropped, so a malformed optional section degrades
 * gracefully instead of discarding the whole card; a payload missing its
 * required `orderId`/`dataSources`/`order`/`health` is rejected outright since
 * there is nothing safe to render (per NFR 3.3, callers should still surface a
 * visible warning rather than silently swallowing this case).
 */
export function extractOrderInsight(raw: unknown): OrderInsightPayload | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const p = raw as Record<string, unknown>;

  const orderId = typeof p.orderId === 'string' ? p.orderId : undefined;
  const dataSourcesRaw = p.dataSources as Record<string, unknown> | undefined;
  const orderRaw = p.order as Record<string, unknown> | undefined;
  const healthRaw = p.health as Record<string, unknown> | undefined;

  if (!orderId || !dataSourcesRaw || !orderRaw || !healthRaw) return undefined;
  if (typeof healthRaw.classification !== 'string') return undefined;

  const lines = Array.isArray(orderRaw.lines) ? (orderRaw.lines as OrderInsightPayload['order']['lines']) : [];

  return {
    orderId,
    dataSources: {
      oms: dataSourcesRaw.oms === 'LIVE' ? 'LIVE' : 'UNAVAILABLE',
      wms: dataSourcesRaw.wms === 'LIVE' ? 'LIVE' : 'UNAVAILABLE',
      tms: dataSourcesRaw.tms === 'LIVE' ? 'LIVE' : 'UNAVAILABLE',
    },
    order: {
      customerName: typeof orderRaw.customerName === 'string' ? orderRaw.customerName : '',
      companyName: typeof orderRaw.companyName === 'string' ? orderRaw.companyName : '',
      accountTier: typeof orderRaw.accountTier === 'string' ? orderRaw.accountTier : undefined,
      orderDate: typeof orderRaw.orderDate === 'string' ? orderRaw.orderDate : '',
      slaDate: typeof orderRaw.slaDate === 'string' ? orderRaw.slaDate : '',
      channel: typeof orderRaw.channel === 'string' ? orderRaw.channel : '',
      paymentStatus: typeof orderRaw.paymentStatus === 'string' ? orderRaw.paymentStatus : '',
      lines,
    },
    health: {
      classification: healthRaw.classification as OrderInsightPayload['health']['classification'],
      summary: typeof healthRaw.summary === 'string' ? healthRaw.summary : '',
    },
    insights: Array.isArray(p.insights) ? (p.insights as OrderInsightPayload['insights']) : [],
    rootCauses: Array.isArray(p.rootCauses) ? (p.rootCauses as OrderInsightPayload['rootCauses']) : [],
    recommendations: Array.isArray(p.recommendations)
      ? (p.recommendations as OrderInsightPayload['recommendations'])
      : [],
    actions: (p.actions as OrderInsightPayload['actions']) ?? {},
  };
}
